'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from '@/shared/navigation'
import { dispatchDeliveryToDelman } from '@/features/kitchen/api/dispatchDelivery'
import { generateDispatchDeliveries } from '@/features/kitchen/api/generateDispatchDeliveries'
import {
  fetchKitchenDelmans,
  fetchKitchenOutlets,
  fetchKitchenStockLots,
  fetchOutletPlan,
  type ApiOutlet,
  type ApiOutletPlanLine,
} from '@/features/kitchen/api/getDispatchOptions'
import { fetchTodayDeliveries } from '@/features/kitchen/api/getTodayDispatch'
import { downloadTodayDispatchPdf } from '@/features/kitchen/api/downloadTodayDispatchPdf'
import { saveDispatchDelivery } from '@/features/kitchen/api/saveDispatchDelivery'
import { shouldUseKitchenApi } from '@/features/kitchen/api/kitchenApiMode'
import { mapDeliveriesFromApi, type ApiShipment } from '@/features/kitchen/api/mapDispatchFromApi'
import { loadDispatchShipments, saveDispatchShipments } from '@/features/kitchen/storage/dispatchStorage'
import { ActionSummaryCard } from '@/shared/components/ActionSummaryCard'
import { FlowStepper } from '@/shared/components/FlowStepper'
import { SkeletonList } from '@/shared/components/Skeleton'
import { QtyStepper } from '@/shared/components/QtyStepper'
import { StickyActionFooter } from '@/shared/components/StickyActionFooter'
import { PinKeypadModal } from '@/shared/components/PinKeypadModal'
import { Toast } from '@/shared/components/Toast'
import { getKitchenDevice } from '@/shared/auth/deviceStorage'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { consumeLastSyncQueued } from '@/shared/sync/kitchenSync'

type ShipmentStatus = 'draft' | 'prepared' | 'in_transit' | 'received'

type ShipmentLine = {
  id: string
  productName: string
  productionDate: string
  qty: number
}

type Shipment = {
  id: string
  deliveryId?: string
  storeName: string
  delmanName: string
  status: ShipmentStatus
  lines: ShipmentLine[]
  updatedAt: string
}

type StockItem = {
  productName: string
  productionDate: string
  stockAvailable: number
}

type View = 'list' | 'edit'
type EditorStep = 'edit' | 'review'
type ListFilter = 'pending' | 'in_transit' | 'received'

const DEFAULT_OUTLETS = ['Toko Menteng', 'Toko Kemang', 'Toko Cipete', 'Toko Senayan', 'Toko BSD'] as const

const MOCK_OUTLET_PLANS: Record<string, ApiOutletPlanLine[]> = {
  'Toko Menteng': [
    { productName: 'Roti coklat', planQty: 40 },
    { productName: 'Donat meses', planQty: 35 },
  ],
  'Toko Kemang': [
    { productName: 'Roti coklat', planQty: 30 },
    { productName: 'Roti keju', planQty: 25 },
  ],
  'Toko Cipete': [
    { productName: 'Donat meses', planQty: 28 },
    { productName: 'Roti keju', planQty: 20 },
  ],
  'Toko Senayan': [{ productName: 'Roti coklat', planQty: 50 }],
  'Toko BSD': [
    { productName: 'Roti coklat', planQty: 35 },
    { productName: 'Donat meses', planQty: 30 },
    { productName: 'Roti keju', planQty: 22 },
  ],
}

function sumPlanLines(lines: ApiOutletPlanLine[]): number {
  return lines.reduce((sum, line) => sum + line.planQty, 0)
}

function outletHasHoPlan(outlet: string, plans: Record<string, ApiOutletPlanLine[]>): boolean {
  return (plans[outlet]?.length ?? 0) > 0
}

const DEFAULT_DELMANS = ['Budi Santoso', 'Andi Wijaya', 'Candra Pratama'] as const

const DISPATCH_FLOW_STEPS = [
  { label: 'Atur kirim' },
  { label: 'Serah delman' },
  { label: 'Toko terima' },
] as const

function dispatchFlowActiveStep(
  status: ShipmentStatus,
  editorStep: EditorStep,
): number {
  if (status === 'received') return DISPATCH_FLOW_STEPS.length + 1
  if (status === 'in_transit') return 3
  if (editorStep === 'review') return 2
  return 1
}

const BASE_STOCK: StockItem[] = [
  { productName: 'Roti coklat', productionDate: '23/06/26', stockAvailable: 120 },
  { productName: 'Roti keju', productionDate: '23/06/26', stockAvailable: 80 },
  { productName: 'Donat gula', productionDate: '23/06/26', stockAvailable: 45 },
  { productName: 'Croissant', productionDate: '23/06/26', stockAvailable: 24 },
  { productName: 'Roti coklat', productionDate: '22/06/26', stockAvailable: 30 },
]

function stockKey(productName: string, productionDate: string): string {
  return `${productName}|${productionDate}`
}

function shipmentStatusLabel(status: ShipmentStatus): string {
  if (status === 'draft') return 'Belum disimpan'
  if (status === 'prepared') return 'Belum diserahkan'
  if (status === 'in_transit') return 'Sudah ke delman'
  return 'Toko sudah terima'
}

function shipmentStatusBadge(status: ShipmentStatus): string {
  if (status === 'draft') return 'badge-draft'
  if (status === 'prepared') return 'badge-warning'
  if (status === 'in_transit') return 'badge-warning'
  return 'badge-success'
}

function shipmentStatusShortLabel(status: ShipmentStatus): string {
  if (status === 'draft') return 'Draft'
  if (status === 'prepared') return 'Siap serah'
  if (status === 'in_transit') return 'Ke delman'
  return 'Toko terima'
}

function sortShipmentsForDisplay(shipments: Shipment[]): Shipment[] {
  const rank: Record<ShipmentStatus, number> = {
    prepared: 0,
    draft: 1,
    in_transit: 2,
    received: 3,
  }
  return [...shipments].sort((a, b) => {
    if (rank[a.status] !== rank[b.status]) return rank[a.status] - rank[b.status]
    return a.storeName.localeCompare(b.storeName, 'id')
  })
}

function groupShipmentsByDelman(shipments: Shipment[]): { delmanName: string; shipments: Shipment[] }[] {
  const order: string[] = []
  const map = new Map<string, Shipment[]>()

  for (const shipment of shipments) {
    if (!map.has(shipment.delmanName)) {
      map.set(shipment.delmanName, [])
      order.push(shipment.delmanName)
    }
    map.get(shipment.delmanName)!.push(shipment)
  }

  return order.map((delmanName) => ({
    delmanName,
    shipments: sortShipmentsForDisplay(map.get(delmanName)!),
  }))
}

function formatHandoverTime(iso?: string): string {
  if (!iso) return ''
  const date = new Date(iso)
  const day = new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: '2-digit' }).format(date)
  const time = new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(date)
  return `Diserahkan ${day} ${time}`
}

function formatSavedAt(): string {
  const now = new Date()
  const day = new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: '2-digit' }).format(now)
  const time = new Intl.DateTimeFormat('id-ID', { hour: '2-digit', minute: '2-digit' }).format(now)
  return `Disimpan ${day} ${time}`
}

function getFilledLines(lines: ShipmentLine[]): ShipmentLine[] {
  return lines.filter((line) => line.qty > 0)
}

function pickBestStockLot(
  productName: string,
  stockItems: StockItem[],
  remainingStock: Map<string, number>,
): StockItem | undefined {
  const candidates = stockItems.filter((item) => item.productName === productName)
  if (candidates.length === 0) return undefined

  return [...candidates].sort((a, b) => {
    const availA = remainingStock.get(stockKey(a.productName, a.productionDate)) ?? 0
    const availB = remainingStock.get(stockKey(b.productName, b.productionDate)) ?? 0
    if (availB !== availA) return availB - availA
    return b.productionDate.localeCompare(a.productionDate)
  })[0]
}

function buildShipmentLinesFromOutletPlan(
  planLines: ApiOutletPlanLine[],
  stockItems: StockItem[],
  remainingStock: Map<string, number>,
  storeName: string,
  shipments: Shipment[],
  excludeShipmentId?: string | null,
): ShipmentLine[] {
  const seen = new Set<string>()
  const lines: ShipmentLine[] = []
  const baseId = Date.now()

  for (const [index, plan] of planLines.entries()) {
    if (seen.has(plan.productName)) continue
    seen.add(plan.productName)

    const lot = pickBestStockLot(plan.productName, stockItems, remainingStock)
    const fallbackDate = stockItems.find((item) => item.productName === plan.productName)?.productionDate
    const productionDate = lot?.productionDate ?? fallbackDate ?? '-'

    const planRemaining = Math.max(
      0,
      plan.planQty -
        qtyUsedForOutletProduct(shipments, storeName, plan.productName, { excludeShipmentId }),
    )

    let qty = 0
    if (lot && planRemaining > 0) {
      const stockAvail = getRemainingQtyForProduct(
        remainingStock,
        lot.productName,
        stockItems,
        lot.productionDate,
      )
      qty = Math.min(planRemaining, stockAvail)
    }

    lines.push({
      id: `line-${baseId}-${index}`,
      productName: plan.productName,
      productionDate,
      qty,
    })
  }

  return lines
}

function buildShipmentLinesFromStock(
  stockItems: StockItem[],
  remainingStock: Map<string, number>,
): ShipmentLine[] {
  const baseId = Date.now()

  return stockItems
    .filter((item) => (remainingStock.get(stockKey(item.productName, item.productionDate)) ?? 0) > 0)
    .map((item, index) => ({
      id: `line-${baseId}-${index}`,
      productName: item.productName,
      productionDate: item.productionDate,
      qty: 0,
    }))
}

function buildInitialShipmentLines(
  planLines: ApiOutletPlanLine[],
  stockItems: StockItem[],
  shipments: Shipment[],
  storeName: string,
  excludeShipmentId?: string | null,
  useLedgerStock = false,
): ShipmentLine[] {
  const remaining = computeRemainingStock(
    shipments,
    excludeShipmentId,
    stockItems,
    useLedgerStock,
  )
  if (planLines.length > 0) {
    return buildShipmentLinesFromOutletPlan(
      planLines,
      stockItems,
      remaining,
      storeName,
      shipments,
      excludeShipmentId,
    )
  }
  return buildShipmentLinesFromStock(stockItems, remaining)
}

function isEditableStatus(status: ShipmentStatus): boolean {
  return status === 'draft' || status === 'prepared'
}

function isActiveAtKitchen(status: ShipmentStatus): boolean {
  return status === 'draft' || status === 'prepared'
}

function getStockItemForProduct(
  productName: string,
  productionDate: string | undefined,
  baseStock: StockItem[],
): StockItem | undefined {
  if (productionDate) {
    const exact = baseStock.find(
      (item) => item.productName === productName && item.productionDate === productionDate,
    )
    if (exact) return exact
  }
  return baseStock.find((item) => item.productName === productName)
}

function getRemainingQtyForProduct(
  remaining: Map<string, number>,
  productName: string,
  stockItems: StockItem[],
  productionDate?: string,
): number {
  const item = getStockItemForProduct(productName, productionDate, stockItems)
  if (!item) return 0
  return Math.max(0, remaining.get(stockKey(item.productName, item.productionDate)) ?? 0)
}

function qtyUsedForOutletProduct(
  shipments: Shipment[],
  storeName: string,
  productName: string,
  options?: { excludeShipmentId?: string | null; excludeLineId?: string },
): number {
  return shipments
    .filter((s) => s.storeName === storeName && s.id !== options?.excludeShipmentId)
    .flatMap((s) => getFilledLines(s.lines))
    .filter((l) => l.productName === productName && l.id !== options?.excludeLineId)
    .reduce((sum, l) => sum + l.qty, 0)
}

/** Stok tersisa. Mode ledger: stok API sudah neto; kurangi hanya draft/siap serah yang belum keluar ledger. */
function computeRemainingStock(
  shipments: Shipment[],
  excludeShipmentId?: string | null,
  baseStock: StockItem[] = BASE_STOCK,
  subtractOnlyDraftAndPrepared = false,
): Map<string, number> {
  const remaining = new Map(
    baseStock.map((item) => [stockKey(item.productName, item.productionDate), item.stockAvailable]),
  )

  for (const shipment of shipments) {
    if (shipment.id === excludeShipmentId) continue
    if (subtractOnlyDraftAndPrepared && !isActiveAtKitchen(shipment.status)) continue
    for (const line of getFilledLines(shipment.lines)) {
      const stockItem = getStockItemForProduct(line.productName, line.productionDate, baseStock)
      if (!stockItem) continue
      const key = stockKey(stockItem.productName, stockItem.productionDate)
      remaining.set(key, (remaining.get(key) ?? 0) - line.qty)
    }
  }

  return remaining
}

function getLineStockLimit(
  shipments: Shipment[],
  shipmentId: string,
  productName: string,
  productionDate: string,
  baseStock: StockItem[] = BASE_STOCK,
  subtractOnlyDraftAndPrepared = false,
): number {
  const base = getStockItemForProduct(productName, productionDate, baseStock)
  if (!base) return 0

  const remaining = computeRemainingStock(
    shipments,
    shipmentId,
    baseStock,
    subtractOnlyDraftAndPrepared,
  )
  const key = stockKey(base.productName, base.productionDate)
  return Math.max(0, remaining.get(key) ?? base.stockAvailable)
}

export function KitchenDispatchPage() {
  const useApi = shouldUseKitchenApi()
  const device = getKitchenDevice()
  const [shipments, setShipments] = useState<Shipment[]>(() =>
    useApi ? [] : loadDispatchShipments(),
  )
  const [isLoading, setIsLoading] = useState(useApi)
  const [view, setView] = useState<View>('list')
  const [listFilter, setListFilter] = useState<ListFilter>('pending')
  const [editorStep, setEditorStep] = useState<EditorStep>('edit')
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(null)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [outletOptions, setOutletOptions] = useState<string[]>(useApi ? [] : [...DEFAULT_OUTLETS])
  const [delmanOptions, setDelmanOptions] = useState<string[]>(useApi ? [] : [...DEFAULT_DELMANS])
  const [stockItems, setStockItems] = useState<StockItem[]>(useApi ? [] : BASE_STOCK)
  const [outletList, setOutletList] = useState<ApiOutlet[]>([])
  const [outletPlan, setOutletPlan] = useState<Record<string, number> | null>(null)
  const [outletPlansByName, setOutletPlansByName] = useState<Record<string, ApiOutletPlanLine[]>>({})
  const [isLoadingCreateOutletPlans, setIsLoadingCreateOutletPlans] = useState(false)
  const [isStockOpen, setIsStockOpen] = useState(false)
  const [selectedOutlets, setSelectedOutlets] = useState<string[]>([])
  const [newDelman, setNewDelman] = useState<string>(useApi ? '' : DEFAULT_DELMANS[0])
  const [createError, setCreateError] = useState<string | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [isDispatching, setIsDispatching] = useState(false)
  const [isPinModalOpen, setIsPinModalOpen] = useState(false)
  const [isSavingShipment, setIsSavingShipment] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false)

  useEffect(() => {
    if (useApi) return
    saveDispatchShipments(shipments)
  }, [shipments, useApi])

  const reloadDeliveries = useCallback(() => {
    if (!useApi || !device?.deviceId) return Promise.resolve()
    return fetchTodayDeliveries(device.deviceId)
      .then((data) => {
        const mapped: Shipment[] = mapDeliveriesFromApi(data.deliveries).map((s: ApiShipment) => ({
          id: s.id,
          deliveryId: s.deliveryId,
          storeName: s.storeName,
          delmanName: s.delmanName,
          status: s.status,
          lines: s.lines,
          updatedAt: s.updatedAt,
        }))
        setShipments(mapped)
      })
      .catch(() => setShipments([]))
  }, [useApi, device?.deviceId])

  useEffect(() => {
    if (!useApi || !device?.deviceId) {
      setIsLoading(false)
      return
    }

    let cancelled = false
    setIsLoading(true)
    reloadDeliveries().finally(() => {
      if (!cancelled) setIsLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId, reloadDeliveries])

  useEffect(() => {
    if (!useApi || !device?.deviceId) return undefined
    const hasInTransit = shipments.some((s) => s.status === 'in_transit')
    if (!hasInTransit) return undefined
    const timer = window.setInterval(() => void reloadDeliveries(), 5_000)
    return () => window.clearInterval(timer)
  }, [useApi, device?.deviceId, shipments, reloadDeliveries])

  useEffect(() => {
    if (!useApi || !device?.deviceId) return

    let cancelled = false
    Promise.all([
      fetchKitchenOutlets(device.deviceId),
      fetchKitchenDelmans(device.deviceId),
    ])
      .then(([outlets, delmans]) => {
        if (cancelled) return
        setOutletList(outlets)
        setOutletOptions(outlets.map((o) => o.name))
        setDelmanOptions(delmans.map((d) => d.name))
      })
      .catch(() => {
        if (cancelled) return
        setOutletList([])
        setOutletOptions([])
        setDelmanOptions([])
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId])

  const reloadStock = useCallback(() => {
    if (!useApi || !device?.deviceId) return
    fetchKitchenStockLots(device.deviceId)
      .then((stock) => {
        setStockItems(
          stock.map((s) => ({
            productName: s.productName,
            productionDate: s.productionDate,
            stockAvailable: s.stockAvailable,
          })),
        )
      })
      .catch(() => setStockItems([]))
  }, [useApi, device?.deviceId])

  useEffect(() => {
    reloadStock()
  }, [reloadStock, shipments])

  useEffect(() => {
    if (isCreateOpen) reloadStock()
  }, [isCreateOpen, reloadStock])

  useEffect(() => {
    const onFocus = () => {
      reloadStock()
      void reloadDeliveries()
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        reloadStock()
        void reloadDeliveries()
      }
    }
    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [reloadStock, reloadDeliveries])

  useEffect(() => {
    if (!useApi || !device?.deviceId || !selectedShipmentId) {
      setOutletPlan(null)
      return
    }
    const shipment = shipments.find((s) => s.id === selectedShipmentId)
    const outlet = outletList.find((o) => o.name === shipment?.storeName)
    if (!shipment || !outlet) {
      setOutletPlan(null)
      return
    }

    let cancelled = false
    fetchOutletPlan(device.deviceId, outlet.id)
      .then((lines) => {
        if (cancelled) return
        const map: Record<string, number> = {}
        for (const line of lines) map[line.productName] = line.planQty
        setOutletPlan(map)
      })
      .catch(() => {
        if (!cancelled) setOutletPlan(null)
      })

    return () => {
      cancelled = true
    }
  }, [useApi, device?.deviceId, selectedShipmentId, shipments, outletList])

  useEffect(() => {
    if (!selectedShipmentId) return
    const shipment = shipments.find((s) => s.id === selectedShipmentId)
    if (!shipment) return
    if (shipment.lines.length > 0) return
    if (!isEditableStatus(shipment.status)) return
    // Mode API: qty awal hanya dari Generate distribusi, bukan isi penuh per produk.
    if (useApi) return

    const planLines: ApiOutletPlanLine[] = useApi
      ? outletPlan
        ? Object.entries(outletPlan).map(([productName, planQty]) => ({ productName, planQty }))
        : []
      : MOCK_OUTLET_PLANS[shipment.storeName] ?? []

    if (useApi && outletPlan === null) return

    const lines = buildInitialShipmentLines(
      planLines,
      stockItems,
      shipments,
      shipment.storeName,
      selectedShipmentId,
      useApi,
    )
    if (lines.length === 0) return

    setShipments((prev) =>
      prev.map((s) => (s.id === selectedShipmentId ? { ...s, lines } : s)),
    )
  }, [selectedShipmentId, outletPlan, useApi, stockItems, shipments])

  useEffect(() => {
    if (!isCreateOpen) return

    if (!useApi) {
      setOutletPlansByName(MOCK_OUTLET_PLANS)
      return
    }

    if (!device?.deviceId || outletList.length === 0) {
      setOutletPlansByName({})
      return
    }

    let cancelled = false
    setIsLoadingCreateOutletPlans(true)
    Promise.all(
      outletList.map(async (outlet) => {
        const lines = await fetchOutletPlan(device.deviceId, outlet.id)
        return [outlet.name, lines] as const
      }),
    )
      .then((entries) => {
        if (cancelled) return
        setOutletPlansByName(Object.fromEntries(entries))
      })
      .catch(() => {
        if (!cancelled) setOutletPlansByName({})
      })
      .finally(() => {
        if (!cancelled) setIsLoadingCreateOutletPlans(false)
      })

    return () => {
      cancelled = true
    }
  }, [isCreateOpen, useApi, device?.deviceId, outletList])

  useEffect(() => {
    if (!isCreateOpen || isLoadingCreateOutletPlans) return
    setSelectedOutlets((prev) => prev.filter((name) => outletHasHoPlan(name, outletPlansByName)))
  }, [isCreateOpen, isLoadingCreateOutletPlans, outletPlansByName])

  const selectableOutlets = useMemo(
    () => outletOptions.filter((outlet) => outletHasHoPlan(outlet, outletPlansByName)),
    [outletOptions, outletPlansByName],
  )

  const selectedCreatePlanTotal = useMemo(() => {
    return selectedOutlets.reduce((sum, outlet) => sum + sumPlanLines(outletPlansByName[outlet] ?? []), 0)
  }, [selectedOutlets, outletPlansByName])

  const kitchenName = device?.kitchenName ?? 'Dapur'

  const today = useMemo(
    () =>
      new Intl.DateTimeFormat('id-ID', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      }).format(new Date()),
    [],
  )

  const dismissToast = useCallback(() => setToast(null), [])
  const showToast = useCallback((message: string) => setToast(message), [])

  async function handleDownloadDispatchPdf() {
    if (!useApi) {
      showToast('Unduh PDF membutuhkan koneksi API.')
      return
    }
    setIsDownloadingPdf(true)
    try {
      await downloadTodayDispatchPdf()
      showToast('PDF distribusi berhasil diunduh.')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Gagal unduh PDF distribusi.')
    } finally {
      setIsDownloadingPdf(false)
    }
  }

  const selectedShipment = shipments.find((s) => s.id === selectedShipmentId) ?? null

  const pendingShipments = useMemo(
    () => shipments.filter((s) => isActiveAtKitchen(s.status)),
    [shipments],
  )
  const inTransitShipments = useMemo(
    () => shipments.filter((s) => s.status === 'in_transit'),
    [shipments],
  )
  const receivedShipments = useMemo(
    () => shipments.filter((s) => s.status === 'received'),
    [shipments],
  )

  const visibleShipments = useMemo(() => {
    if (listFilter === 'pending') return sortShipmentsForDisplay(pendingShipments)
    if (listFilter === 'in_transit') return sortShipmentsForDisplay(inTransitShipments)
    return sortShipmentsForDisplay(receivedShipments)
  }, [listFilter, pendingShipments, inTransitShipments, receivedShipments])

  const preparedPending = useMemo(
    () => pendingShipments.filter((s) => s.status === 'prepared'),
    [pendingShipments],
  )
  const draftPending = useMemo(
    () => pendingShipments.filter((s) => s.status === 'draft'),
    [pendingShipments],
  )

  const remainingStock = useMemo(
    () => computeRemainingStock(shipments, selectedShipmentId, stockItems, useApi),
    [shipments, selectedShipmentId, stockItems, useApi],
  )

  const visibleStockChips = useMemo(() => {
    return stockItems.map((item) => {
      const key = stockKey(item.productName, item.productionDate)
      const available = remainingStock.get(key) ?? 0
      return { ...item, key, available }
    }).filter((item) => item.available > 0)
  }, [remainingStock, stockItems])

  /** Sisa rencana untuk produk ini ke outlet terpilih (cap distribusi). null = tidak ada cap rencana. */
  function planRemainingForProduct(productName: string, excludeLineId?: string): number | null {
    if (!outletPlan || !selectedShipment) return null
    const planQty = outletPlan[productName] ?? 0
    const used = qtyUsedForOutletProduct(shipments, selectedShipment.storeName, productName, {
      excludeLineId,
    })
    return Math.max(0, planQty - used)
  }

  function getStockLimitForLine(line: ShipmentLine): number {
    if (!selectedShipmentId) return 0
    const stockLimit = getLineStockLimit(
      shipments,
      selectedShipmentId,
      line.productName,
      line.productionDate,
      stockItems,
      useApi,
    )
    const planRemaining = planRemainingForProduct(line.productName, line.id)
    if (planRemaining === null) return stockLimit
    return Math.min(stockLimit, planRemaining)
  }

  function openCreateModal() {
    setSelectedOutlets([])
    setNewDelman(delmanOptions[0] ?? '')
    setCreateError(null)
    setIsCreateOpen(true)
  }

  function toggleOutletSelection(outlet: string) {
    if (!outletHasHoPlan(outlet, outletPlansByName)) return
    setSelectedOutlets((prev) =>
      prev.includes(outlet) ? prev.filter((name) => name !== outlet) : [...prev, outlet],
    )
    setCreateError(null)
  }

  async function generateShipments() {
    if (selectedOutlets.length === 0 || !newDelman) {
      setCreateError(
        selectableOutlets.length === 0
          ? 'Belum ada toko dengan rencana HO hari ini.'
          : 'Pilih minimal satu toko dan delman.',
      )
      return
    }
    if (!useApi || !device?.deviceId) {
      setCreateError('Generate distribusi membutuhkan koneksi API.')
      return
    }

    setIsGenerating(true)
    setCreateError(null)
    try {
      const result = await generateDispatchDeliveries({
        deviceId: device.deviceId,
        outletNames: selectedOutlets,
        delmanName: newDelman,
      })
      await reloadDeliveries()
      setIsCreateOpen(false)
      setListFilter('pending')
      const skipped = result.skipped_outlets?.length ?? 0
      showToast(
        skipped > 0
          ? `${result.deliveries.length} kiriman dibuat/diperbarui. ${skipped} toko dilewati.`
          : `${result.deliveries.length} kiriman distribusi digenerate. Periksa jumlah per toko.`,
      )
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Gagal generate distribusi.')
    } finally {
      setIsGenerating(false)
    }
  }

  function openShipment(id: string) {
    setSelectedShipmentId(id)
    setEditorStep('edit')
    setView('edit')
    setSaveError(null)
  }

  function closeShipment() {
    setSelectedShipmentId(null)
    setEditorStep('edit')
    setView('list')
    setSaveError(null)
  }

  function updateLine(lineId: string, qty: number) {
    if (!selectedShipmentId) return
    setShipments((prev) =>
      prev.map((s) => {
        if (s.id !== selectedShipmentId) return s
        return {
          ...s,
          lines: s.lines.map((line) =>
            line.id === lineId ? { ...line, qty: Math.max(0, qty) } : line,
          ),
        }
      }),
    )
  }

  function removeLine(lineId: string) {
    if (!selectedShipmentId) return
    setShipments((prev) =>
      prev.map((s) =>
        s.id === selectedShipmentId
          ? { ...s, lines: s.lines.filter((line) => line.id !== lineId) }
          : s,
      ),
    )
  }

  function canDeleteShipment(shipment: Shipment): boolean {
    return shipment.status === 'draft' && getFilledLines(shipment.lines).length === 0
  }

  function deleteShipment(id: string) {
    const shipment = shipments.find((s) => s.id === id)
    if (!shipment || !canDeleteShipment(shipment)) return
    setShipments((prev) => prev.filter((s) => s.id !== id))
    if (selectedShipmentId === id) closeShipment()
    showToast('Pengiriman kosong dihapus.')
  }

  function validateLines(lines: ShipmentLine[]): boolean {
    const filled = getFilledLines(lines)
    if (filled.length === 0) {
      setSaveError('Tambahkan minimal 1 produk dengan jumlah lebih dari 0.')
      return false
    }
    const overStock = filled.find((line) => line.qty > getStockLimitForLine(line))
    if (overStock) {
      const limit = getStockLimitForLine(overStock)
      setSaveError(
        `${overStock.productName}: maks ${limit} pcs (stok dapur tersisa setelah pengiriman toko lain). Kurangi jumlah atau produksi dulu.`,
      )
      return false
    }
    setSaveError(null)
    return true
  }

  async function persistShipmentToServer(shipment: Shipment): Promise<Shipment> {
    if (!useApi || !device?.deviceId) return shipment

    const filled = getFilledLines(shipment.lines)
    const result = await saveDispatchDelivery({
      deliveryId: shipment.id,
      deviceId: device.deviceId,
      storeName: shipment.storeName,
      delmanName: shipment.delmanName,
      lines: filled.map((line) => ({
        productName: line.productName,
        productionDate: line.productionDate,
        qty: line.qty,
      })),
    })

    return {
      ...shipment,
      id: result.delivery_code || shipment.id,
      deliveryId: result.delivery_id,
      status: 'prepared',
      updatedAt: result.updated_at,
    }
  }

  async function saveShipmentDraft() {
    if (!selectedShipmentId || !selectedShipment) return
    if (!validateLines(selectedShipment.lines)) return

    setIsSavingShipment(true)
    try {
      let saved = selectedShipment
      if (useApi) {
        saved = await persistShipmentToServer(selectedShipment)
      } else {
        saved = {
          ...selectedShipment,
          status: 'prepared',
          updatedAt: formatSavedAt(),
        }
      }

      setShipments((prev) =>
        prev.map((s) => (s.id === selectedShipmentId ? saved : s)),
      )
      setSaveError(null)
      closeShipment()
      setListFilter('pending')
      showToast(
        `Pengiriman disimpan. Bisa dilanjutkan nanti.${consumeLastSyncQueued() ? ` ${kitchenTerms.sync.queued}` : ''}`,
      )
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Gagal menyimpan pengiriman.')
    } finally {
      setIsSavingShipment(false)
    }
  }

  async function openHandoverReview() {
    if (!selectedShipmentId || !selectedShipment) return
    if (!validateLines(selectedShipment.lines)) return

    setIsSavingShipment(true)
    try {
      let saved = selectedShipment
      if (useApi) {
        saved = await persistShipmentToServer(selectedShipment)
        setShipments((prev) =>
          prev.map((s) => (s.id === selectedShipmentId ? saved : s)),
        )
      } else {
        setShipments((prev) =>
          prev.map((s) =>
            s.id === selectedShipmentId
              ? { ...s, status: 'prepared', updatedAt: formatSavedAt() }
              : s,
          ),
        )
      }
      setEditorStep('review')
      setSaveError(null)
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Gagal menyimpan pengiriman.')
    } finally {
      setIsSavingShipment(false)
    }
  }

  async function confirmHandoverToDelman(pin: string) {
    if (!selectedShipment || !isEditableStatus(selectedShipment.status)) return
    if (!validateLines(selectedShipment.lines)) return

    setIsDispatching(true)
    try {
      const filled = getFilledLines(selectedShipment.lines)
      const result = await dispatchDeliveryToDelman({
        deliveryId: selectedShipment.id,
        deviceId: device?.deviceId ?? '',
        pin,
        storeName: selectedShipment.storeName,
        delmanName: selectedShipment.delmanName,
        lines: filled.map((line) => ({
          productName: line.productName,
          productionDate: line.productionDate,
          qty: line.qty,
        })),
      })

      const handoverLabel = formatHandoverTime(result.dispatched_at)
      const totalQty = filled.reduce((sum, line) => sum + line.qty, 0)

      setShipments((prev) =>
        prev.map((s) =>
          s.id === selectedShipment.id
            ? { ...s, status: 'in_transit' as const, updatedAt: handoverLabel }
            : s,
        ),
      )

      setIsPinModalOpen(false)
      showToast(
        `${totalQty} pcs ke ${selectedShipment.storeName} diserahkan ke ${selectedShipment.delmanName}.${consumeLastSyncQueued() ? ` ${kitchenTerms.sync.queued}` : ''}`,
      )
      closeShipment()
      setListFilter('in_transit')
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Gagal menyerahkan ke delman.')
      throw err
    } finally {
      setIsDispatching(false)
    }
  }

  function renderShipmentCard(shipment: Shipment) {
    const totalQty = getFilledLines(shipment.lines).reduce((sum, line) => sum + line.qty, 0)
    const deletable = canDeleteShipment(shipment)
    const needsAction = isActiveAtKitchen(shipment.status)
    const isReadyHandover = shipment.status === 'prepared'

    return (
      <div
        key={shipment.id}
        className={`card ${isReadyHandover ? 'border-status-warning/40 bg-status-warning-bg/30' : ''}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-label-md font-mono text-on-surface-variant">{shipment.id}</p>
            <p className="text-body-lg font-bold text-on-surface mt-1">{shipment.storeName}</p>
            <p className="text-body-sm text-on-surface-variant mt-1">Delman {shipment.delmanName}</p>
            <p className="text-body-sm text-on-surface-variant mt-0.5">
              {totalQty > 0
                ? `${getFilledLines(shipment.lines).length} produk · ${totalQty} pcs`
                : 'Belum ada produk ditambahkan'}
            </p>
            <p className="text-label-md text-on-surface-variant mt-2">{shipment.updatedAt}</p>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            <span className={shipmentStatusBadge(shipment.status)}>
              {shipmentStatusShortLabel(shipment.status)}
            </span>
            {deletable && (
              <button
                type="button"
                onClick={() => deleteShipment(shipment.id)}
                className="text-status-danger p-1"
                aria-label="Hapus pengiriman kosong"
              >
                <span className="material-symbols-outlined text-xl">delete</span>
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={() => openShipment(shipment.id)}
          className={`mt-3 w-full min-h-[44px] ${needsAction ? 'btn-primary' : 'btn-outline'}`}
        >
          {isReadyHandover ? 'Serahkan ke delman' : needsAction ? 'Atur pengiriman' : 'Lihat detail'}
        </button>
      </div>
    )
  }

  function renderGroupedShipments(shipments: Shipment[]) {
    if (shipments.length === 0) return null

    return groupShipmentsByDelman(shipments).map((group) => (
      <div key={group.delmanName} className="space-y-2">
        <div className="flex items-center gap-2 px-1 pt-1">
          <span className="material-symbols-outlined text-primary text-lg">local_shipping</span>
          <p className="text-body-sm font-semibold text-on-surface">{group.delmanName}</p>
          <span className="badge-draft">{group.shipments.length} toko</span>
        </div>
        {group.shipments.map((shipment) => renderShipmentCard(shipment))}
      </div>
    ))
  }

  const filledLines = selectedShipment ? getFilledLines(selectedShipment.lines) : []
  const filledTotalQty = filledLines.reduce((sum, line) => sum + line.qty, 0)
  const isEditable = selectedShipment ? isEditableStatus(selectedShipment.status) : false

  const listEmptyMessage: Record<ListFilter, string> = {
    pending: 'Belum ada pengiriman yang perlu disiapkan.',
    in_transit: 'Belum ada barang yang diserahkan ke delman.',
    received: 'Belum ada toko yang selesai menerima.',
  }

  return (
    <section className="space-y-4">
      <div className="card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-headline-md text-primary">Distribusi ke toko</p>
            <p className="text-body-sm text-on-surface-variant mt-1 capitalize">
              {kitchenName} · {today}
            </p>
          </div>
          {useApi && (
            <button
              type="button"
              className="btn-outline min-h-[44px] shrink-0 inline-flex items-center gap-2"
              onClick={handleDownloadDispatchPdf}
              disabled={isDownloadingPdf}
            >
              <span className="material-symbols-outlined text-lg">download</span>
              {isDownloadingPdf ? 'Mengunduh…' : 'Unduh PDF'}
            </button>
          )}
        </div>
        <p className="text-body-sm text-on-surface-variant mt-3 pt-3 border-t border-surface-variant">
          Dapur menentukan toko tujuan, produk, dan jumlah kirim. Pilih delman yang mengantar.
        </p>
      </div>

      {view === 'list' && (
        <div className="space-y-3">
          {isLoading && <SkeletonList count={3} label="Memuat pengiriman hari ini" />}
          <div className="card space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-body-sm text-on-surface-variant">Pengiriman hari ini</p>
              <button type="button" className="btn-primary" onClick={openCreateModal}>
                + Generate distribusi
              </button>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {[
                { key: 'pending' as const, label: 'Belum diserahkan', count: pendingShipments.length },
                { key: 'in_transit' as const, label: 'Sudah ke delman', count: inTransitShipments.length },
                { key: 'received' as const, label: 'Toko terima', count: receivedShipments.length },
              ].map((filter) => (
                <button
                  key={filter.key}
                  type="button"
                  onClick={() => setListFilter(filter.key)}
                  className={`rounded-lg px-2 py-2 text-body-sm font-semibold leading-tight ${
                    listFilter === filter.key
                      ? 'bg-primary-container text-on-primary-container'
                      : 'bg-surface text-on-surface-variant border border-outline-variant'
                  }`}
                >
                  {filter.label}
                  {filter.count > 0 && <span className="block text-xs opacity-80 mt-0.5">({filter.count})</span>}
                </button>
              ))}
            </div>
          </div>

          {listFilter === 'pending' ? (
            <>
              {preparedPending.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-1">
                    <p className="text-body-sm font-bold text-status-warning">Siap diserahkan ke delman</p>
                    <span className="badge-warning">{preparedPending.length}</span>
                  </div>
                  {renderGroupedShipments(preparedPending)}
                </div>
              )}
              {draftPending.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-1">
                    <p className="text-body-sm font-bold text-on-surface-variant">Perlu dilengkapi</p>
                    <span className="badge-draft">{draftPending.length}</span>
                  </div>
                  {renderGroupedShipments(draftPending)}
                </div>
              )}
            </>
          ) : (
            renderGroupedShipments(visibleShipments)
          )}

          {visibleShipments.length === 0 && (
            <div className="card text-center space-y-3 text-body-sm text-on-surface-variant">
              <p>{listEmptyMessage[listFilter]}</p>
              {listFilter === 'pending' && (
                <button type="button" className="btn-primary w-full sm:w-auto" onClick={openCreateModal}>
                  + Generate distribusi
                </button>
              )}
            </div>
          )}

          <Link href="/kitchen/home" className="btn-outline block text-center">
            Kembali ke beranda
          </Link>
        </div>
      )}

      {view === 'edit' && selectedShipment && editorStep === 'edit' && (
        <div className={isEditable ? 'relative pb-28' : undefined}>
          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
              <div>
                <p className="font-label-md font-mono text-on-surface-variant">{selectedShipment.id}</p>
                <p className="text-headline-md text-primary mt-1">{selectedShipment.storeName}</p>
                <p className="text-body-sm text-on-surface-variant">
                  Delman {selectedShipment.delmanName} · {shipmentStatusLabel(selectedShipment.status)}
                </p>
              </div>
              <button type="button" className="btn-outline min-h-[44px]" onClick={closeShipment}>
                Kembali
              </button>
            </div>

            <FlowStepper
              steps={[...DISPATCH_FLOW_STEPS]}
              activeStep={dispatchFlowActiveStep(selectedShipment.status, editorStep)}
            />

            {isEditable && visibleStockChips.length > 0 && (
              <div className="rounded-lg bg-surface-container p-3 space-y-2">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 text-left"
                  onClick={() => setIsStockOpen((v) => !v)}
                  aria-expanded={isStockOpen}
                >
                  <span className="text-label-md font-semibold text-on-surface-variant">
                    Stok dapur tersedia
                    <span className="ml-1 text-on-surface-variant/80">
                      ({visibleStockChips.length} produk)
                    </span>
                  </span>
                  <span className="material-symbols-outlined text-on-surface-variant">
                    {isStockOpen ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                {isStockOpen && (
                  <>
                    <div className="flex max-h-44 flex-wrap gap-2 overflow-y-auto">
                      {visibleStockChips.map((item) => (
                        <span
                          key={item.key}
                          className="text-body-sm text-on-surface bg-surface-card border border-outline-variant rounded-lg px-2.5 py-1.5"
                        >
                          {item.productName}{' '}
                          <span className="text-on-surface-variant">({item.productionDate})</span>{' '}
                          <strong>{item.available} pcs</strong>
                        </span>
                      ))}
                    </div>
                    <p className="text-label-md text-on-surface-variant">
                      Total per produk (tanggal = batch terbaru), sudah dikurangi qty pengiriman lain hari ini.
                    </p>
                  </>
                )}
              </div>
            )}

            <div className="rounded-lg bg-surface-container p-3 text-body-sm space-y-1">
              <p>
                <span className="text-on-surface-variant">Toko:</span>{' '}
                <strong className="text-on-surface">{selectedShipment.storeName}</strong>
              </p>
              <p>
                <span className="text-on-surface-variant">Delman:</span>{' '}
                <strong className="text-on-surface">{selectedShipment.delmanName}</strong>
              </p>
              {isEditable && (
                <p className="text-label-md text-on-surface-variant pt-1">
                  Toko & delman ditetapkan saat membuat pengiriman.
                </p>
              )}
            </div>

          {selectedShipment.lines.length === 0 && isEditable && (
            <div className="rounded-lg border border-dashed border-outline-variant p-4 text-center text-body-sm text-on-surface-variant">
              Belum ada produk di rencana HO atau stok dapur untuk toko ini.
            </div>
          )}

          <div className="space-y-2">
            {selectedShipment.lines.map((line) => {
              const stockLimit = getStockLimitForLine(line)
              const overStock = isEditable && line.qty > stockLimit
              return (
                <div key={line.id} className="rounded-lg border border-outline-variant p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-body-lg font-semibold text-on-surface">{line.productName}</p>
                      <p className="text-label-md text-on-surface-variant">
                        Produksi {line.productionDate}
                        {isEditable && <span> · Maks kirim {stockLimit} pcs</span>}
                      </p>
                      {isEditable && outletPlan && (
                        <p className="text-label-md text-on-surface-variant">
                          Rencana ke {selectedShipment.storeName}: {outletPlan[line.productName] ?? 0} pcs
                          {' · '}
                          Sisa rencana {planRemainingForProduct(line.productName, line.id) ?? 0} pcs
                        </p>
                      )}
                    </div>
                    {isEditable && (
                      <button
                        type="button"
                        onClick={() => removeLine(line.id)}
                        className="text-status-danger"
                        aria-label="Hapus produk"
                      >
                        <span className="material-symbols-outlined">delete</span>
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <label className="text-body-sm text-on-surface-variant">Jumlah kirim</label>
                    <QtyStepper
                      value={line.qty}
                      min={0}
                      max={isEditable ? stockLimit : undefined}
                      onChange={(qty) => updateLine(line.id, qty)}
                      disabled={!isEditable}
                    />
                    <span className="text-body-sm text-on-surface-variant">pcs</span>
                  </div>
                  {overStock && (
                    <p className="text-label-md text-status-danger font-medium">
                      Melebihi maks kirim ({stockLimit} pcs)
                    </p>
                  )}
                </div>
              )
            })}
          </div>

          {filledLines.length > 0 && (
            <div className="rounded-lg bg-surface-container p-3 flex items-center justify-between">
              <span className="text-body-sm text-on-surface-variant">Total kirim</span>
              <span className="text-body-lg font-bold text-on-surface">
                {filledLines.length} produk · {filledTotalQty} pcs
              </span>
            </div>
          )}

          {saveError && (
            <div role="alert" className="alert-error">
              <span className="material-symbols-outlined text-base shrink-0">error</span>
              <span>{saveError}</span>
            </div>
          )}

          {selectedShipment.status === 'received' && (
            <p className="text-body-sm text-on-surface-variant text-center">
              Toko sudah menerima barang. Tidak bisa diubah.
            </p>
          )}
          </div>

          {isEditable && (
            <StickyActionFooter>
              <button
                type="button"
                className="btn-outline w-full min-h-[44px]"
                onClick={saveShipmentDraft}
                disabled={isSavingShipment || isDispatching}
              >
                {isSavingShipment ? 'Menyimpan…' : 'Simpan pengiriman'}
              </button>
              <button
                type="button"
                className="btn-primary w-full min-h-[44px]"
                onClick={openHandoverReview}
                disabled={isSavingShipment || isDispatching}
              >
                Serahkan ke delman
              </button>
              <p className="text-label-md text-on-surface-variant text-center">
                Simpan dulu kalau belum selesai. Serah ke delman = final, tidak bisa diubah.
              </p>
            </StickyActionFooter>
          )}
        </div>
      )}

      {view === 'edit' && selectedShipment && editorStep === 'review' && (
        <div className="relative pb-28">
          <div className="card space-y-3">
            <div className="flex items-center justify-between gap-2 border-b border-surface-variant pb-3">
              <div>
                <p className="font-label-md font-mono text-on-surface-variant">{selectedShipment.id}</p>
                <p className="text-headline-md text-primary mt-1">Konfirmasi serah ke delman</p>
                <p className="text-body-sm text-on-surface-variant">{selectedShipment.storeName}</p>
              </div>
              <button type="button" className="btn-outline min-h-[44px]" onClick={() => setEditorStep('edit')}>
                Kembali
              </button>
            </div>

            <FlowStepper
              steps={[...DISPATCH_FLOW_STEPS]}
              activeStep={dispatchFlowActiveStep(selectedShipment.status, editorStep)}
            />

            <p className="text-body-sm text-on-surface-variant">
              Periksa pengiriman sebelum diserahkan. Setelah dikonfirmasi, data tidak bisa diubah.
            </p>

            <div className="rounded-lg bg-surface-container p-3 text-body-sm space-y-1">
              <p>
                <span className="text-on-surface-variant">Toko:</span>{' '}
                <strong className="text-on-surface">{selectedShipment.storeName}</strong>
              </p>
              <p>
                <span className="text-on-surface-variant">Delman:</span>{' '}
                <strong className="text-on-surface">{selectedShipment.delmanName}</strong>
              </p>
            </div>

            <ActionSummaryCard
              items={filledLines.map((line) => ({
                id: line.id,
                title: line.productName,
                subtitle: `Produksi ${line.productionDate}`,
                value: `${line.qty} pcs`,
              }))}
              totalLabel="Total"
              totalValue={`${filledLines.length} produk · ${filledTotalQty} pcs`}
            />

            {saveError && (
              <div role="alert" className="alert-error">
                <span className="material-symbols-outlined text-base shrink-0">error</span>
                <span>{saveError}</span>
              </div>
            )}
          </div>

          <StickyActionFooter>
            <button
              type="button"
              className="btn-primary w-full min-h-[44px]"
              onClick={() => setIsPinModalOpen(true)}
              disabled={isDispatching}
            >
              Konfirmasi dengan PIN
            </button>
          </StickyActionFooter>
        </div>
      )}

      {isCreateOpen && (
        <div className="fixed inset-0 z-30 bg-inverse-surface/40 flex items-center justify-center p-4">
          <div className="w-full max-w-md max-h-[90vh] flex flex-col bg-surface-card border border-outline-variant rounded-xl shadow-sm">
            <div className="px-4 py-3 border-b border-surface-variant flex items-center justify-between shrink-0">
              <p className="text-headline-md text-primary">Generate distribusi</p>
              <button
                type="button"
                onClick={() => setIsCreateOpen(false)}
                className="text-on-surface-variant"
                aria-label="Tutup"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-4 space-y-4 overflow-y-auto flex-1">
              <p className="text-body-sm text-on-surface-variant">
                Pilih beberapa toko dan delman. Jika stok tidak cukup, qty dibagi proporsional per produk
                (bukan per toko dapat stok penuh). Kiriman draft yang sudah ada akan diperbarui.
              </p>
              <div className="space-y-2">
                <p className="field-label">Toko tujuan</p>
                {outletOptions.length === 0 ? (
                  <p className="text-body-sm text-on-surface-variant rounded-lg border border-dashed border-outline-variant p-3">
                    Belum ada outlet terhubung dengan dapur ini.
                  </p>
                ) : isLoadingCreateOutletPlans ? (
                  <p className="text-body-sm text-on-surface-variant">Memuat rencana toko…</p>
                ) : (
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {outletOptions.map((outlet) => {
                      const checked = selectedOutlets.includes(outlet)
                      const planLines = outletPlansByName[outlet] ?? []
                      const hasPlan = planLines.length > 0
                      return (
                        <button
                          key={outlet}
                          type="button"
                          onClick={() => toggleOutletSelection(outlet)}
                          disabled={!hasPlan}
                          className={`w-full text-left rounded-lg border p-3 min-h-[44px] transition-colors ${
                            !hasPlan
                              ? 'border-outline-variant/60 bg-surface-container-low opacity-60 cursor-not-allowed'
                              : checked
                                ? 'border-primary bg-primary-container/20'
                                : 'border-outline-variant bg-surface-container-low'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <span
                              className={`material-symbols-outlined text-lg mt-0.5 ${
                                !hasPlan
                                  ? 'text-on-surface-variant/60'
                                  : checked
                                    ? 'text-primary'
                                    : 'text-on-surface-variant'
                              }`}
                            >
                              {!hasPlan
                                ? 'block'
                                : checked
                                  ? 'check_box'
                                  : 'check_box_outline_blank'}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-body-sm font-semibold text-on-surface">{outlet}</p>
                              <p className="text-label-md text-on-surface-variant mt-0.5">
                                {hasPlan
                                  ? `${sumPlanLines(planLines)} pcs rencana HO`
                                  : 'Tidak ada rencana HO hari ini — tidak bisa dipilih'}
                              </p>
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
                {selectedOutlets.length > 0 && !isLoadingCreateOutletPlans && (
                  <p className="text-label-md text-on-surface-variant">
                    {selectedOutlets.length} toko terpilih · {selectedCreatePlanTotal} pcs rencana HO (gabungan)
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <p className="field-label">Delman pengantar</p>
                {delmanOptions.length === 0 ? (
                  <p className="text-body-sm text-on-surface-variant rounded-lg border border-dashed border-outline-variant p-3">
                    Belum ada delman terhubung dengan dapur ini.
                  </p>
                ) : (
                  <div className="grid grid-cols-1 gap-2">
                    {delmanOptions.map((delman) => (
                      <button
                        key={delman}
                        type="button"
                        onClick={() => setNewDelman(delman)}
                        className={`rounded-lg px-3 py-2.5 text-body-sm font-semibold min-h-[44px] text-left ${
                          newDelman === delman
                            ? 'bg-primary-container text-on-primary-container'
                            : 'bg-surface text-on-surface-variant border border-outline-variant'
                        }`}
                      >
                        {delman}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {createError && (
                <div role="alert" className="alert-error">
                  <span className="material-symbols-outlined text-base shrink-0">error</span>
                  <span>{createError}</span>
                </div>
              )}
            </div>
            <div className="px-4 py-3 border-t border-surface-variant flex gap-2 justify-end shrink-0">
              <button type="button" className="btn-outline" onClick={() => setIsCreateOpen(false)}>
                Batal
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => void generateShipments()}
                disabled={isGenerating || isLoadingCreateOutletPlans}
              >
                {isGenerating ? 'Mengenerate…' : 'Generate'}
              </button>
            </div>
          </div>
        </div>
      )}

      <PinKeypadModal
        open={isPinModalOpen}
        title="PIN konfirmasi"
        description={`Masukkan PIN 6 digit untuk ${kitchenTerms.dispatch.handover.toLowerCase()}.`}
        confirmLabel="Konfirmasi serah ke delman"
        onClose={() => setIsPinModalOpen(false)}
        onConfirm={confirmHandoverToDelman}
      />

      {toast && <Toast message={toast} onDismiss={dismissToast} />}
    </section>
  )
}
